import React, { useState, useEffect, useCallback } from 'react';
import useAuthStore from '../lib/auth-store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Landmark, CheckCircle2, Clock, XCircle, FileText, RefreshCcw,
  ExternalLink, Plus, Wifi, WifiOff, ChevronRight, Building2,
  CreditCard, Search, AlertTriangle, Bell, ShieldAlert, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  New: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
  'In Progress': { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  Approved: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  Declined: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
  Cancelled: { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: XCircle },
  Pending: { color: 'bg-violet-100 text-violet-800 border-violet-200', icon: Clock },
  Signed: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
  Unsigned: { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertTriangle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.New;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`} data-testid={`mav-status-${status}`}>
      {status}
    </span>
  );
}

export default function MaverickPage() {
  const { token, user } = useAuthStore();
  const [tab, setTab] = useState('boarding');
  const [status, setStatus] = useState(null);
  const [boarding, setBoarding] = useState([]);
  const [ach, setAch] = useState([]);
  const achAccounts = boardedAchAccounts(boarding);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [webhookStats, setWebhookStats] = useState(null);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'iso';

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const checkStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/maverick/status`, { headers });
      const data = await res.json();
      setStatus(data);
    } catch { setStatus({ connected: false }); }
  }, [token]);

  const loadBoarding = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/maverick/boarding`, { headers });
      const data = await res.json();
      setBoarding(data.items || []);
    } catch { setBoarding([]); }
    setLoading(false);
  }, [token]);

  const loadACH = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/maverick/ach`, { headers });
      const data = await res.json();
      setAch(data.items || []);
    } catch { setAch([]); }
  }, [token]);

  const loadWebhooks = useCallback(async () => {
    try {
      const [eventsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/maverick/webhooks/events?limit=50`, { headers }),
        fetch(`${API}/api/maverick/webhooks/stats`, { headers }),
      ]);
      const events = await eventsRes.json();
      const stats = await statsRes.json();
      setWebhookEvents(events.items || []);
      setWebhookStats(stats);
    } catch {}
  }, [token]);

  useEffect(() => {
    checkStatus();
    loadBoarding();
    loadACH();
    loadWebhooks();
  }, [checkStatus, loadBoarding, loadACH, loadWebhooks]);

  const viewApp = async (appId) => {
    try {
      const res = await fetch(`${API}/api/maverick/boarding/${appId}`, { headers });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setSelectedApp(data);
      setDetailOpen(true);
    } catch (err) { toast.error('Failed to load application details'); }
  };

  const filteredBoarding = boarding.filter(a => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (a.company?.name || '').toLowerCase().includes(s) ||
      (a.dba?.name || '').toLowerCase().includes(s) ||
      String(a.id).includes(s);
  });

  const acknowledgeEvent = async (eventId) => {
    try {
      await fetch(`${API}/api/maverick/webhooks/events/${eventId}/acknowledge`, {
        method: 'PUT', headers
      });
      toast.success('Event acknowledged');
      loadWebhooks();
    } catch { toast.error('Failed to acknowledge'); }
  };

  const acknowledgeAll = async () => {
    try {
      const res = await fetch(`${API}/api/maverick/webhooks/events/acknowledge-all`, {
        method: 'PUT', headers
      });
      const data = await res.json();
      toast.success(`${data.acknowledged} events acknowledged`);
      loadWebhooks();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="maverick-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[var(--font-heading)]">Processor Integration</h1>
          <p className="text-sm text-muted-foreground mt-1">TSYS-msc — Boarding, ACH & Transactions</p>
        </div>
        <div className="flex items-center gap-3">
          {status && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
              status.connected ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'
            }`} data-testid="maverick-connection-status">
              {status.connected ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
              {status.connected ? `Connected (${status.boarding_applications} apps)` : 'Disconnected'}
              {status.sandbox && <Badge variant="secondary" className="ml-1 text-[9px] py-0">SANDBOX</Badge>}
            </div>
          )}
          {isAdmin && (
            <Button onClick={() => setCreateOpen(true)} data-testid="new-boarding-btn">
              <Plus className="h-4 w-4 mr-2" /> New Application
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit" data-testid="maverick-tabs">
        {[
          { key: 'boarding', label: 'Boarding Apps', icon: FileText, count: boarding.length },
          { key: 'ach', label: 'ACH – E-Check', icon: CreditCard, count: achAccounts.length },
          { key: 'webhooks', label: 'Webhook Events', icon: Bell, count: webhookStats?.unacknowledged || 0, alert: webhookStats?.critical_unack > 0 },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              tab === t.key ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
            {t.count > 0 && (
              <Badge variant={t.alert ? 'destructive' : 'secondary'} className="text-[10px] px-1.5 py-0">
                {t.count}
              </Badge>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      {tab === 'boarding' && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search by company, DBA, or ID..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9" data-testid="maverick-search" />
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : tab === 'boarding' ? (
        <BoardingTable apps={filteredBoarding} onView={viewApp} />
      ) : tab === 'ach' ? (
        <ACHAccountsTable apps={achAccounts} onView={viewApp} />
      ) : (
        <WebhookEventsPanel
          events={webhookEvents}
          stats={webhookStats}
          onAcknowledge={acknowledgeEvent}
          onAcknowledgeAll={acknowledgeAll}
        />
      )}

      {/* Boarding Detail Dialog */}
      <BoardingDetailDialog app={selectedApp} open={detailOpen} onClose={() => setDetailOpen(false)} />

      {/* Create Boarding Dialog */}
      <CreateBoardingDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        token={token}
        onCreated={() => { loadBoarding(); setCreateOpen(false); }}
      />
    </div>
  );
}

function BoardingTable({ apps, onView }) {
  if (apps.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed rounded-xl">
        <Landmark className="h-10 w-10 mx-auto text-muted-foreground/20 mb-2" />
        <p className="text-sm text-muted-foreground">No boarding applications found</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="boarding-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="text-left px-4 py-3 font-medium">ID</th>
              <th className="text-left px-4 py-3 font-medium">Company / DBA</th>
              <th className="text-left px-4 py-3 font-medium">Method</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Signature</th>
              <th className="text-left px-4 py-3 font-medium">Created</th>
              <th className="text-center px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {apps.map(app => (
              <tr key={app.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors" data-testid={`boarding-row-${app.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">#{app.id}</td>
                <td className="px-4 py-3">
                  <div>
                    <p className="font-medium text-sm">{app.company?.name || '—'}</p>
                    {app.dba?.name && app.dba.name !== app.company?.name && (
                      <p className="text-xs text-muted-foreground">DBA: {app.dba.name}</p>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className="text-[10px]">{app.processingMethod}</Badge>
                </td>
                <td className="px-4 py-3"><StatusBadge status={app.status} /></td>
                <td className="px-4 py-3"><StatusBadge status={app.signature?.status || 'Unsigned'} /></td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {app.createdOn ? new Date(app.createdOn).toLocaleDateString() : '—'}
                </td>
                <td className="px-4 py-3 text-center">
                  <Button variant="ghost" size="sm" onClick={() => onView(app.id)} data-testid={`view-boarding-${app.id}`}>
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 border-t bg-muted/30 text-xs text-muted-foreground">
        {apps.length} boarding application{apps.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}

const BOARDED = /approv|board|active|live|complete/i;
// Boarded merchant accounts that process ACH / e-check (method ACH or All): accounts, not transactions.
const boardedAchAccounts = (apps) => apps.filter((a) => /ach|all/i.test(a.processingMethod || '') && BOARDED.test(a.status || ''));

function ACHAccountsTable({ apps, onView }) {
  if (!apps.length) return <p className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="ach-accounts-empty">No boarded ACH / e-check merchant accounts yet</p>;
  return (
    <div className="overflow-hidden rounded-xl border bg-card" data-testid="ach-accounts-table">
      <table className="w-full text-sm">
        <thead><tr className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
          <th className="px-4 py-3">Merchant</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Boarded</th><th className="px-4 py-3" /></tr></thead>
        <tbody>{apps.map((a) => (
          <tr key={a.id} className="border-b last:border-0" data-testid={`ach-account-${a.id}`}>
            <td className="px-4 py-3 font-medium">{a.company?.name || a.dba?.name || `#${a.id}`}</td>
            <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
            <td className="px-4 py-3 text-xs text-muted-foreground">{(a.updatedOn || a.createdOn) ? new Date(a.updatedOn || a.createdOn).toLocaleDateString() : '—'}</td>
            <td className="px-4 py-3 text-right"><Button variant="ghost" size="sm" onClick={() => onView(a.id)} data-testid={`view-ach-account-${a.id}`}><ExternalLink className="h-4 w-4" /></Button></td>
          </tr>))}</tbody>
      </table>
      <div className="border-t bg-muted/30 px-4 py-3 text-xs text-muted-foreground">{apps.length} boarded ACH account{apps.length !== 1 ? 's' : ''}</div>
    </div>
  );
}

function ACHTable({ transactions }) {
  if (transactions.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed rounded-xl">
        <CreditCard className="h-10 w-10 mx-auto text-muted-foreground/20 mb-2" />
        <p className="text-sm text-muted-foreground">No ACH transactions found</p>
        <p className="text-xs text-muted-foreground mt-1">Transactions will appear here once merchants are boarded.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="ach-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="text-left px-4 py-3 font-medium">ID</th>
              <th className="text-left px-4 py-3 font-medium">Amount</th>
              <th className="text-left px-4 py-3 font-medium">Type</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Customer</th>
              <th className="text-left px-4 py-3 font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map(tx => (
              <tr key={tx.id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`ach-row-${tx.id}`}>
                <td className="px-4 py-3 font-mono text-xs">#{tx.id}</td>
                <td className="px-4 py-3 font-semibold font-mono">${(tx.amount || 0).toFixed(2)}</td>
                <td className="px-4 py-3"><Badge variant="outline">{tx.transactionType}</Badge></td>
                <td className="px-4 py-3"><StatusBadge status={tx.status} /></td>
                <td className="px-4 py-3 text-sm">
                  {tx.customer ? `${tx.customer.firstName || ''} ${tx.customer.lastName || ''}`.trim() || tx.customer.email : '—'}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {tx.createdOn ? new Date(tx.createdOn).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BoardingDetailDialog({ app, open, onClose }) {
  if (!app) return null;

  const company = app.company || {};
  const dba = app.dba || {};
  const principal = app.principals?.[0] || {};
  const addr = company.address || {};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="boarding-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="w-5 h-5" />
            Boarding Application #{app.id}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Status */}
          <div className="flex items-center justify-between">
            <StatusBadge status={app.status} />
            <div className="flex items-center gap-2">
              <Badge variant="outline">{app.processingMethod}</Badge>
              <StatusBadge status={app.signature?.status || 'Unsigned'} />
            </div>
          </div>

          {/* Company Info */}
          <div className="rounded-lg border p-3 space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" /> Company
            </h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-xs text-muted-foreground">Legal Name:</span><br />{company.name || '—'}</div>
              <div><span className="text-xs text-muted-foreground">DBA:</span><br />{dba.name || '—'}</div>
              <div><span className="text-xs text-muted-foreground">Phone:</span><br />{company.phone || '—'}</div>
              <div><span className="text-xs text-muted-foreground">Email:</span><br />{company.email || '—'}</div>
              {addr.street && (
                <div className="col-span-2"><span className="text-xs text-muted-foreground">Address:</span><br />
                  {addr.street}, {addr.city} {addr.zip}
                </div>
              )}
            </div>
          </div>

          {/* Principal */}
          {principal.firstName && (
            <div className="rounded-lg border p-3 space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground">Principal</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-xs text-muted-foreground">Name:</span><br />{principal.firstName} {principal.lastName}</div>
                <div><span className="text-xs text-muted-foreground">Title:</span><br />{principal.title || '—'}</div>
                <div><span className="text-xs text-muted-foreground">SSN:</span><br />{principal.ssn ? '••••' + principal.ssn.slice(-4) : '—'}</div>
                <div><span className="text-xs text-muted-foreground">Ownership:</span><br />{principal.ownership || '—'}%</div>
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Created: {app.createdOn ? new Date(app.createdOn).toLocaleString() : '—'}</span>
            <span>Updated: {app.updatedOn ? new Date(app.updatedOn).toLocaleString() : '—'}</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="boarding-detail-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateBoardingDialog({ open, onClose, token, onCreated }) {
  const [method, setMethod] = useState('Acquiring');
  const [campaignId, setCampaignId] = useState('8');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/maverick/boarding`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign_id: parseInt(campaignId),
          processing_method: method,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create');
      }
      toast.success('Boarding application created on TSYS-msc');
      onCreated();
    } catch (err) { toast.error(err.message); }
    setCreating(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="create-boarding-dialog">
        <DialogHeader>
          <DialogTitle>New Boarding Application</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Processing Method</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="boarding-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Acquiring">Acquiring (Card Processing)</SelectItem>
                <SelectItem value="ACH">ACH</SelectItem>
                <SelectItem value="All">All Methods</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Campaign ID</label>
            <Input value={campaignId} onChange={e => setCampaignId(e.target.value)} placeholder="8" data-testid="boarding-campaign-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating} data-testid="create-boarding-submit">
            {creating ? 'Creating...' : 'Create on TSYS-msc'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const SEVERITY_STYLES = {
  critical: { bg: 'bg-red-50 border-red-200', dot: 'bg-red-500', text: 'text-red-800', badge: 'bg-red-100 text-red-700' },
  high: { bg: 'bg-orange-50 border-orange-200', dot: 'bg-orange-500', text: 'text-orange-800', badge: 'bg-orange-100 text-orange-700' },
  medium: { bg: 'bg-amber-50 border-amber-200', dot: 'bg-amber-500', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-700' },
  low: { bg: 'bg-gray-50 border-gray-200', dot: 'bg-gray-400', text: 'text-gray-700', badge: 'bg-gray-100 text-gray-600' },
};

const MODULE_ICONS = {
  ach: CreditCard,
  chargeback: ShieldAlert,
  transaction: Landmark,
};

function WebhookEventsPanel({ events, stats, onAcknowledge, onAcknowledgeAll }) {
  const [filter, setFilter] = useState('all');

  const filtered = events.filter(e => {
    if (filter === 'unack') return !e.acknowledged;
    if (filter === 'critical') return e.severity === 'critical' && !e.acknowledged;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="webhook-events-panel">
      {/* Stats Bar */}
      {stats && (
        <div className="flex flex-wrap items-center gap-3" data-testid="webhook-stats">
          <div className="flex items-center gap-4 px-4 py-2.5 rounded-lg border bg-card">
            <div className="text-center">
              <div className="text-lg font-bold">{stats.total}</div>
              <div className="text-[10px] text-muted-foreground">Total</div>
            </div>
            <div className="w-px h-8 bg-border" />
            <div className="text-center">
              <div className="text-lg font-bold text-amber-600">{stats.unacknowledged}</div>
              <div className="text-[10px] text-muted-foreground">Pending</div>
            </div>
            <div className="w-px h-8 bg-border" />
            <div className="text-center">
              <div className="text-lg font-bold text-red-600">{stats.critical_unack}</div>
              <div className="text-[10px] text-muted-foreground">Critical</div>
            </div>
          </div>

          {/* Module breakdown */}
          <div className="flex items-center gap-2">
            {Object.entries(stats.by_module || {}).map(([mod, count]) => {
              const Icon = MODULE_ICONS[mod] || Bell;
              return (
                <span key={mod} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-muted text-xs">
                  <Icon className="w-3 h-3" /> {mod}: {count}
                </span>
              );
            })}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="webhook-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="unack">Unacknowledged</SelectItem>
                <SelectItem value="critical">Critical Only</SelectItem>
              </SelectContent>
            </Select>
            {stats.unacknowledged > 0 && (
              <Button variant="outline" size="sm" onClick={onAcknowledgeAll} data-testid="ack-all-btn">
                <Check className="w-3.5 h-3.5 mr-1.5" /> Ack All
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Events List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 border border-dashed rounded-xl">
          <Bell className="h-10 w-10 mx-auto text-muted-foreground/20 mb-2" />
          <p className="text-sm text-muted-foreground">No webhook events yet</p>
          <p className="text-xs text-muted-foreground mt-1">Events from TSYS-msc will appear here in real-time.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((event, i) => {
            const sev = SEVERITY_STYLES[event.severity] || SEVERITY_STYLES.low;
            const Icon = MODULE_ICONS[event.module] || Bell;
            const time = event.received_at ? new Date(event.received_at).toLocaleString() : '—';

            return (
              <div key={event.event_id || i}
                className={`rounded-lg border p-3 transition-all ${event.acknowledged ? 'opacity-50 bg-muted/20 border-border' : sev.bg}`}
                data-testid={`webhook-event-${event.event_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${sev.badge}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-xs font-bold uppercase ${sev.text}`}>{event.severity}</span>
                      <span className="text-[10px] text-muted-foreground">{event.module}:{event.action}</span>
                      {event.acknowledged && <Badge variant="outline" className="text-[9px] py-0">Ack</Badge>}
                    </div>
                    <p className="text-sm font-medium leading-snug">{event.summary}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{time}</p>
                  </div>
                  {!event.acknowledged && (
                    <Button variant="ghost" size="sm" onClick={() => onAcknowledge(event.event_id)}
                      className="flex-shrink-0" data-testid={`ack-event-${event.event_id}`}>
                      <Check className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
